import React from 'react';
import {TouchableOpacity, View} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import SlideModal from '@/Atoms/SlideModal';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import Icon from 'react-native-vector-icons/MaterialIcons';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {openInNewTab} from '@/Utils/CommonUtils';
import styles from './styles';

interface Props {
  openSideModal: boolean;
  setOpenSideModal: (v: boolean) => void;
  orderDetails: any;
  subscriptions: any[];
  invoice: any;
  hoveredSubId: number | null;
  setHoveredSubId: (id: number | null) => void;
  openDropdownId: number | null;
  setOpenDropdownId: (id: number | null) => void;
  setActiveProgressReports: (reports: any[]) => void;
  setShowProgressReportModal: (v: boolean) => void;
  handleResumeSubscription: (orderId: string) => void;
  setPauseOrderId: (id: string) => void;
  setPauseEndDate: (date: Date | null) => void;
  setPauseError: (err: string) => void;
  setPauseLimits: (limits: any) => void;
  setPauseLimitsError: (err: string) => void;
  setShowPauseModal: (v: boolean) => void;
  getPauseLimits: (params: {order_id: string}) => void;
}

const formatSimpleDate = (dateStr: string) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
};

const getSubscriptionBadgeStyle = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return {backgroundColor: '#F0EBE1', textColor: '#5C4A2A'};
    case 'upcoming':
      return {backgroundColor: '#EEF3E2', textColor: '#3D5A1F'};
    case 'expired':
      return {backgroundColor: '#F0F0F0', textColor: '#666666'};
    case 'paused':
      return {backgroundColor: '#FFF3CD', textColor: '#856404'};
    default:
      return {backgroundColor: '#F0F0F0', textColor: '#333333'};
  }
};

const SubscriptionSideModal = ({
  openSideModal,
  setOpenSideModal,
  orderDetails,
  subscriptions,
  invoice,
  hoveredSubId,
  setHoveredSubId,
  openDropdownId,
  setOpenDropdownId,
  setActiveProgressReports,
  setShowProgressReportModal,
  handleResumeSubscription,
  setPauseOrderId,
  setPauseEndDate,
  setPauseError,
  setPauseLimits,
  setPauseLimitsError,
  setShowPauseModal,
  getPauseLimits,
}: Props) => {
  const dropdownItem = (
    borderBottom: boolean,
    icon: string,
    label: string,
    onPress: () => void,
  ) => (
    <Box
      onPress={onPress}
      type="row-flex-start"
      style={[styles.ext, {borderBottomWidth: borderBottom ? 1 : 0}]}>
      <MaterialCommunityIcons
        name={icon as any}
        size={14}
        color={CCColors.TextColor.Dark}
      />
      <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Dark}>
        {label}
      </CCText>
    </Box>
  );
  if (!openSideModal) return null;

  return (
    <SlideModal
      type="side"
      visible={openSideModal}
      showHeader={true}
      customHeaderStyle={{display: 'none'}}
      onClose={() => setOpenSideModal(false)}>
      <View>
        <Box flex={1} style={styles.sideModalHeader}>
          <Box
            style={{margin: 25}}
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center">
            <Box gap={8} alignItems="center" type="row-flex-start">
              <CCText fontSize={FontSizes[20]} fontWeight={400}>
                {orderDetails?.plan_title}
              </CCText>
              <Box
                style={[
                  {
                    backgroundColor: getSubscriptionBadgeStyle(
                      subscriptions?.[0]?.status,
                    ).backgroundColor,
                  },
                  styles.pad,
                ]}>
                <CCText
                  fontSize={FontSizes[9]}
                  fontWeight={600}
                  color={
                    getSubscriptionBadgeStyle(subscriptions?.[0]?.status)
                      .textColor
                  }>
                  {orderDetails?.status}
                </CCText>
              </Box>
            </Box>
            <TouchableOpacity
              onPress={() => setOpenSideModal(false)}
              style={styles.closeiIconWrp}>
              <Box type="center" style={styles.close}>
                <Ionicons name="close-outline" size={24} color="black" />
              </Box>
            </TouchableOpacity>
          </Box>
        </Box>

        <Box style={{padding: 25}}>
          <Box type="row-flex-start" style={styles.fr}>
            <MaterialCommunityIcons
              name="desktop-mac"
              size={16}
              color={CCColors.TextColor.Dark}
            />
            <CCText
              fontWeight={400}
              fontSize={FontSizes[14]}
              color={CCColors.TextColor.LightGrey}>
              {orderDetails?.plan_type}
            </CCText>
          </Box>
          <Box type="row-flex-start" style={styles.gap}>
            <Icon
              name="access-time"
              size={16}
              color={CCColors.TextColor.Dark}
            />
            <CCText
              fontWeight={400}
              fontSize={FontSizes[14]}
              color={CCColors.TextColor.LightGrey}>
              {formatSimpleDate(orderDetails?.subscription_start_date)}
              {' \u2013 '}
              {formatSimpleDate(orderDetails?.subscription_end_date)}
            </CCText>
          </Box>
          <Box type="row-flex-start" style={styles.al}>
            <MaterialCommunityIcons
              name="pound"
              size={16}
              color={CCColors.TextColor.Dark}
            />
            <CCText
              fontWeight={400}
              fontSize={FontSizes[14]}
              color={CCColors.TextColor.LightGrey}>
              {orderDetails?.order_id}
            </CCText>
          </Box>
        </Box>

        {subscriptions?.length > 0 && (
          <Box style={{padding: 25}}>
            {subscriptions.map((sub: any) => {
              const badgeStyle = getSubscriptionBadgeStyle(sub.status);
              const hasCertificate = !!sub?.certificate_url;
              const hasProgressReport = sub?.ptm?.progress_reports?.length > 0;
              const canPause =
                sub?.batch_type?.toLowerCase() === 'personal' &&
                (sub?.status?.toLowerCase() === 'active' ||
                  sub?.status?.toLowerCase() === 'resume');
              const canResume = sub?.status?.toLowerCase() === 'paused';
              const hasDownloads =
                hasCertificate ||
                hasProgressReport ||
                canPause ||
                canResume ||
                invoice;
              const isHovered = hoveredSubId === sub?.id;
              const isDropdownOpen = openDropdownId === sub?.id;
              return (
                <View
                  key={sub?.id}
                  // @ts-ignore - web-only hover events
                  onMouseEnter={() => setHoveredSubId(sub?.id)}
                  onMouseLeave={() => {
                    setHoveredSubId(null);
                    setOpenDropdownId(null);
                  }}
                  style={{
                    position: 'relative',
                    marginBottom: 10,
                    zIndex: isDropdownOpen ? 10 : 1,
                  }}>
                  <Box style={styles.border}>
                    <Box type="row-center-between">
                      <Box type="row-flex-start" style={styles.flex}>
                        <CCText fontWeight="semibold" fontSize={FontSizes[12]}>
                          {sub?.class_name}
                        </CCText>
                        <Box
                          style={[
                            {backgroundColor: badgeStyle.backgroundColor},
                            styles.pad,
                          ]}>
                          <CCText
                            fontSize={FontSizes[9]}
                            fontWeight="semibold"
                            color={badgeStyle.textColor}>
                            {sub?.status}
                          </CCText>
                        </Box>
                      </Box>
                      {hasDownloads && (isHovered || isDropdownOpen) && (
                        <Box
                          onPress={() =>
                            setOpenDropdownId(isDropdownOpen ? null : sub?.id)
                          }
                          style={styles.bg}>
                          <Icon
                            name="more-vert"
                            size={16}
                            color={CCColors.Black}
                          />
                        </Box>
                      )}
                    </Box>
                    <Spacer spacing={6} />
                    <Box type="row-flex-start" style={styles.mnr}>
                      <Icon
                        name="access-time"
                        size={14}
                        color={CCColors.TextColor.Dark}
                      />
                      <CCText
                        fontWeight="regular"
                        fontSize={FontSizes[10]}
                        color={CCColors.TextColor.Dark}>
                        {formatSimpleDate(sub.subscription_start_date)}
                        {' \u2013 '}
                        {formatSimpleDate(sub.subscription_end_date)}
                      </CCText>
                    </Box>
                  </Box>

                  {isDropdownOpen && (
                    <View style={styles.over}>
                      {hasProgressReport && (
                        <Box
                          onPress={() => {
                            setActiveProgressReports(
                              sub?.ptm?.progress_reports ?? [],
                            );
                            setShowProgressReportModal(true);
                            setOpenSideModal(false);
                            setOpenDropdownId(null);
                          }}
                          type="row-flex-start"
                          style={[
                            {borderBottomWidth: hasCertificate ? 1 : 0},
                            styles.ext,
                          ]}>
                          <MaterialCommunityIcons
                            name="chart-bar"
                            size={14}
                            color={CCColors.TextColor.Dark}
                          />
                          <CCText
                            fontSize={FontSizes[12]}
                            color={CCColors.TextColor.Dark}>
                            Download Progress Report
                          </CCText>
                        </Box>
                      )}
                      {hasCertificate && (
                        <Box
                          onPress={() => {
                            openInNewTab(sub?.certificate_url);
                            setOpenDropdownId(null);
                          }}
                          type="row-flex-start"
                          style={styles.light}>
                          <MaterialCommunityIcons
                            name="certificate-outline"
                            size={14}
                            color={CCColors.TextColor.Dark}
                          />
                          <CCText
                            fontSize={FontSizes[12]}
                            color={CCColors.TextColor.Dark}>
                            Download Certificate
                          </CCText>
                        </Box>
                      )}
                      {invoice && (
                        <Box
                          onPress={() => {
                            setOpenDropdownId(null);
                            openInNewTab((invoice as any)?.invoice_url);
                          }}
                          type="row-flex-start"
                          style={styles.light}>
                          <MaterialCommunityIcons
                            name="file-document-outline"
                            size={14}
                            color={CCColors.TextColor.Dark}
                          />
                          <CCText
                            fontSize={FontSizes[12]}
                            color={CCColors.TextColor.Dark}>
                            Download Invoice
                          </CCText>
                        </Box>
                      )}
                      {canResume &&
                        dropdownItem(
                          canPause,
                          'play-circle-outline',
                          'Resume a class',
                          () => {
                            handleResumeSubscription(orderDetails?.order_id);
                            setOpenDropdownId(null);
                            setOpenSideModal(false);
                          },
                        )}
                      {canPause &&
                        dropdownItem(
                          false,
                          'pause-circle-outline',
                          'Pause a class',
                          () => {
                            setPauseOrderId(orderDetails?.order_id);
                            setPauseEndDate(null);
                            setPauseError('');
                            setPauseLimits(null);
                            setPauseLimitsError('');
                            setShowPauseModal(true);
                            getPauseLimits({order_id: orderDetails?.order_id});
                            setOpenDropdownId(null);
                            setOpenSideModal(false);
                          },
                        )}
                    </View>
                  )}
                </View>
              );
            })}
          </Box>
        )}
      </View>
    </SlideModal>
  );
};

export default SubscriptionSideModal;
